'use client';

// MIS ACTUACIONES (área AUTENTICADA del Portal Ciudadano · roles DISCIPLINADO
// y DEFENSOR). Lista GET /portal/mis-actuaciones en una tabla (radicado, tipo,
// fecha, ver documento) y ofrece acciones (Dialogs) para presentar Descargos,
// Pruebas, Nulidades y Recursos; si el usuario es DEFENSOR, además gestionar
// Poder. Cada acción es un multipart (file? + campos) que se envía con el token
// vía los helpers de lib/portal.ts.
//
// El expediente sobre el que se actúa se elige con un selector poblado con los
// expedientes que aparecen en mis-actuaciones (expedienteId + radicado).

import { useEffect, useMemo, useState } from 'react';
import {
  ExternalLink,
  FileSignature,
  FileText,
  Gavel,
  Loader2,
  Paperclip,
  RefreshCw,
  ScrollText,
  ShieldAlert,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  enviarDescargos,
  enviarNulidad,
  enviarPrueba,
  enviarRecurso,
  gestionarPoder,
  misActuaciones,
  type MiActuacion,
} from '@/lib/portal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// Roles del rol autenticado que pueden actuar como parte en el portal.
const ROL_DEFENSOR = 'DEFENSOR';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

function rolActual(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const u = localStorage.getItem('usuario');
    return u ? (JSON.parse(u).rol as string) : undefined;
  } catch {
    return undefined;
  }
}

// Expediente único (para el selector), derivado de las actuaciones.
interface ExpedienteParte {
  expedienteId: string;
  radicado: string;
}

type Accion = 'descargos' | 'pruebas' | 'nulidades' | 'recursos' | 'poder';

export default function MisActuaciones() {
  const [items, setItems] = useState<MiActuacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [accion, setAccion] = useState<Accion | null>(null);
  const rol = rolActual();
  const esDefensor = rol === ROL_DEFENSOR;

  const expedientes = useMemo<ExpedienteParte[]>(() => {
    const mapa = new Map<string, string>();
    for (const a of items) {
      if (a.expedienteId && !mapa.has(a.expedienteId)) {
        mapa.set(a.expedienteId, a.radicado);
      }
    }
    return Array.from(mapa, ([expedienteId, radicado]) => ({ expedienteId, radicado }));
  }, [items]);

  async function cargar() {
    setCargando(true);
    try {
      const data = await misActuaciones();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('No se pudieron cargar sus actuaciones', {
        description: err instanceof Error ? err.message : undefined,
      });
      setItems([]);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    cargar();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mis actuaciones</h1>
          <p className="text-muted-foreground">
            Consulte las actuaciones de sus procesos y presente escritos.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar} disabled={cargando}>
          <RefreshCw className={cargando ? 'size-4 animate-spin' : 'size-4'} /> Actualizar
        </Button>
      </div>

      {/* Acciones disponibles */}
      <Card>
        <CardHeader>
          <CardTitle>Presentar escritos</CardTitle>
        </CardHeader>
        <CardContent>
          {expedientes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No tiene expedientes asociados para presentar escritos.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setAccion('descargos')}>
                <FileSignature className="size-4" /> Descargos
              </Button>
              <Button variant="outline" onClick={() => setAccion('pruebas')}>
                <Paperclip className="size-4" /> Pruebas
              </Button>
              <Button variant="outline" onClick={() => setAccion('nulidades')}>
                <ShieldAlert className="size-4" /> Nulidades
              </Button>
              <Button variant="outline" onClick={() => setAccion('recursos')}>
                <Gavel className="size-4" /> Recursos
              </Button>
              {esDefensor && (
                <Button variant="outline" onClick={() => setAccion('poder')}>
                  <UserCheck className="size-4" /> Gestionar poder
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla de actuaciones */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScrollText className="size-5 text-primary" /> Actuaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Radicado</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Documento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a, i) => (
                <TableRow key={`${a.expedienteId}-${i}`}>
                  <TableCell className="font-medium">{a.radicado}</TableCell>
                  <TableCell>{a.tipo}</TableCell>
                  <TableCell>{fmtFecha(a.fecha)}</TableCell>
                  <TableCell className="text-right">
                    {a.documentoUrl ? (
                      <a
                        href={a.documentoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2"
                      >
                        <ExternalLink className="size-3.5" /> Ver
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!cargando && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    Sin actuaciones registradas.
                  </TableCell>
                </TableRow>
              )}
              {cargando && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    <Loader2 className="mx-auto size-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {accion && (
        <ModalAccion
          accion={accion}
          expedientes={expedientes}
          onClose={() => setAccion(null)}
          onListo={() => { setAccion(null); cargar(); }}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Modal de acción (descargos / pruebas / nulidades / recursos / poder)
// ===========================================================================

const META: Record<Accion, { titulo: string; icono: typeof FileText; extra?: string }> = {
  descargos: { titulo: 'Presentar descargos', icono: FileSignature },
  pruebas: { titulo: 'Aportar pruebas', icono: Paperclip },
  nulidades: { titulo: 'Solicitar nulidad', icono: ShieldAlert },
  recursos: { titulo: 'Interponer recurso', icono: Gavel },
  poder: { titulo: 'Gestionar poder', icono: UserCheck },
};

function ModalAccion({
  accion,
  expedientes,
  onClose,
  onListo,
}: {
  accion: Accion;
  expedientes: ExpedienteParte[];
  onClose: () => void;
  onListo: () => void;
}) {
  const meta = META[accion];
  const Icono = meta.icono;

  const [expedienteId, setExpedienteId] = useState(expedientes[0]?.expedienteId ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Campos específicos por acción.
  const [texto, setTexto] = useState('');           // descargos
  const [descripcion, setDescripcion] = useState('');// pruebas
  const [causal, setCausal] = useState('');          // nulidades
  const [tipoRecurso, setTipoRecurso] = useState(''); // recursos
  const [defensorNombre, setDefensorNombre] = useState('');       // poder
  const [tarjetaProfesional, setTarjetaProfesional] = useState('');// poder
  const [accionPoder, setAccionPoder] = useState('OTORGAR');       // poder

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    if (!expedienteId) {
      toast.error('Seleccione el expediente.');
      return;
    }
    // Validaciones mínimas por acción.
    if (accion === 'nulidades' && !causal.trim()) {
      toast.error('Indique la causal de nulidad.');
      return;
    }
    if (accion === 'poder' && !file) {
      toast.error('Adjunte el documento del poder.');
      return;
    }

    const fd = new FormData();
    fd.append('expedienteId', expedienteId);
    if (file) fd.append('file', file);

    setEnviando(true);
    try {
      switch (accion) {
        case 'descargos':
          if (texto.trim()) fd.append('texto', texto.trim());
          await enviarDescargos(fd);
          break;
        case 'pruebas':
          if (descripcion.trim()) fd.append('descripcion', descripcion.trim());
          await enviarPrueba(fd);
          break;
        case 'nulidades':
          fd.append('causal', causal.trim());
          await enviarNulidad(fd);
          break;
        case 'recursos':
          if (tipoRecurso.trim()) fd.append('tipoRecurso', tipoRecurso.trim());
          await enviarRecurso(fd);
          break;
        case 'poder':
          fd.append('accion', accionPoder);
          if (defensorNombre.trim()) fd.append('defensorNombre', defensorNombre.trim());
          if (tarjetaProfesional.trim()) fd.append('tarjetaProfesional', tarjetaProfesional.trim());
          await gestionarPoder(fd);
          break;
      }
      toast.success('Escrito enviado.');
      onListo();
    } catch (err) {
      toast.error('No se pudo enviar el escrito', {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icono className="size-5 text-primary" /> {meta.titulo}
          </DialogTitle>
          <DialogDescription>
            Seleccione el expediente y adjunte el documento correspondiente.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={enviar} className="space-y-4">
          <div className="space-y-2">
            <Label>Expediente</Label>
            <Select value={expedienteId} onValueChange={setExpedienteId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="— Seleccione —" />
              </SelectTrigger>
              <SelectContent>
                {expedientes.map((e) => (
                  <SelectItem key={e.expedienteId} value={e.expedienteId}>
                    {e.radicado}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {accion === 'descargos' && (
            <div className="space-y-2">
              <Label htmlFor="texto">Texto de los descargos</Label>
              <Textarea
                id="texto"
                rows={4}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Argumentos de defensa (opcional si adjunta documento)."
              />
            </div>
          )}

          {accion === 'pruebas' && (
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción de la prueba</Label>
              <Textarea
                id="descripcion"
                rows={3}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Describa la prueba que aporta."
              />
            </div>
          )}

          {accion === 'nulidades' && (
            <div className="space-y-2">
              <Label htmlFor="causal">Causal de nulidad *</Label>
              <Textarea
                id="causal"
                rows={3}
                value={causal}
                onChange={(e) => setCausal(e.target.value)}
                placeholder="Indique la causal que fundamenta la nulidad."
              />
            </div>
          )}

          {accion === 'recursos' && (
            <div className="space-y-2">
              <Label>Tipo de recurso</Label>
              <Select value={tipoRecurso} onValueChange={setTipoRecurso}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="— Seleccione —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="REPOSICION">Reposición</SelectItem>
                  <SelectItem value="APELACION">Apelación</SelectItem>
                  <SelectItem value="QUEJA">Notificación disciplinaria</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {accion === 'poder' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="defensorNombre">Nombre del defensor</Label>
                <Input
                  id="defensorNombre"
                  value={defensorNombre}
                  onChange={(e) => setDefensorNombre(e.target.value)}
                  placeholder="Nombre del apoderado"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tarjeta">Tarjeta profesional</Label>
                <Input
                  id="tarjeta"
                  value={tarjetaProfesional}
                  onChange={(e) => setTarjetaProfesional(e.target.value)}
                  placeholder="Número de tarjeta profesional"
                />
              </div>
              <div className="space-y-2">
                <Label>Acción</Label>
                <Select value={accionPoder} onValueChange={setAccionPoder}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OTORGAR">Otorgar poder</SelectItem>
                    <SelectItem value="REVOCAR">Revocar poder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="adjunto">
              Documento (PDF){accion === 'poder' ? ' *' : ' (opcional)'}
            </Label>
            <Input
              id="adjunto"
              type="file"
              accept="application/pdf,image/*,.pdf,.doc,.docx"
              disabled={enviando}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="cursor-pointer"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={enviando}>
              {enviando ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Enviando…
                </>
              ) : 'Enviar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
